/**
 * PayPal express button in the basket (JS SDK popup flow).
 *
 * Markup contract, rendered by the ddhes_paypal module into basket_btn_next_bottom:
 *   <div x-data="paypalExpress({ sdkUrl: '…', errorText: '…', cancelledText: '…' })">
 *     <div x-ref="button"></div>
 *     <div x-show="loading">…</div>
 *     <template x-teleport.prepend="#basketContentsHead">
 *       <div role="alert" x-show="error" x-text="error"></div>
 *
 * The SDK always comes from paypal.com (PayPal forbids bundling or self-hosting it) and
 * is loaded by the component, so a blocked SDK hides the button instead of breaking the
 * page. A click asks the shop to create the PayPal order from the session basket; after
 * the approval in the popup the browser goes to cl=basket&fnc=processPayPalExpressApproval,
 * which creates or logs in the customer and continues to the order step.
 *
 * The customer only ever sees the translated texts: what the shop or PayPal answer goes
 * to the log, never onto the page.
 */
import { loadScript } from '../shell/loadExternal.js';

const endpoint = (fnc) => `/${URLSEGMENT}index.php?cl=paypalexpress_handler&fnc=${fnc}`;

// The handler reads raw JSON from php://input, unlike the form-encoded postAction helper.
async function postJson(fnc, data = {}) {
    const response = await fetch(endpoint(fnc), {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify(data),
    });

    if (!response.ok) {
        throw Object.assign(new Error(`PayPal ${fnc} failed (HTTP ${response.status})`), { status: response.status });
    }

    return response.json();
}

function paypalSdk(sdkUrl) {
    if (window.paypal?.Buttons) {
        return Promise.resolve(window.paypal);
    }

    return loadScript(sdkUrl).then(() => {
        if (!window.paypal?.Buttons) {
            throw new Error('PayPal SDK unavailable');
        }

        return window.paypal;
    });
}

document.addEventListener('alpine:init', () => {
    Alpine.data('paypalExpress', ({ sdkUrl, errorText, cancelledText }) => ({
        available: true,
        loading: false,
        error: '',
        basketGone: false,

        async init() {
            // back from the bfcache the approval redirect is over
            window.addEventListener('pageshow', (event) => {
                if (event.persisted) {
                    this.loading = false;
                }
            });

            let sdk;
            try {
                sdk = await paypalSdk(sdkUrl);
            } catch (error) {
                // ad blocker or consent: the basket stays usable without the button
                console.warn(error);
                this.available = false;
                return;
            }

            sdk.Buttons({
                style: {
                    layout: 'horizontal',
                    color: 'gold',
                    shape: 'rect',
                    label: 'paypal',
                    tagline: false,
                    height: 45,
                },
                createOrder: () => this.createOrder(),
                onApprove: (data) => this.approve(data),
                onCancel: (data) => this.cancel(data),
                onError: (error) => this.fail(error),
                onShippingAddressChange: (data, actions) => this.changeShippingAddress(data, actions),
            }).render(this.$refs.button);
        },

        /** A new attempt starts clean: the message of the last one would contradict the open popup. */
        async createOrder() {
            this.error = '';
            this.loading = true;

            try {
                const answer = await postJson('handleCreatePaypalOrder');
                if (!answer.id) {
                    throw new Error(answer.error || 'PayPal order not created');
                }

                return answer.id;
            } catch (error) {
                // the session basket is empty (expired session, emptied in another tab): show the real basket
                if (error.status === 409) {
                    this.basketGone = true;
                    window.location.reload();
                    throw error;
                }
                this.error = errorText;
                throw error;
            } finally {
                this.loading = false;
            }
        },

        approve({ orderID }) {
            this.error = '';
            // both stay on until the order step is there: a "Zur Kasse" click now would break the redirect
            this.loading = true;
            Alpine.store('basketCheckout').locked = true;

            window.location.href = `/${URLSEGMENT}index.php?cl=basket&fnc=processPayPalExpressApproval`
                + `&dd_paypalreturn=1&orderID=${encodeURIComponent(orderID)}`;
        },

        cancel({ orderID }) {
            this.loading = false;
            this.error = cancelledText;

            // resets the PayPal data in the session; nothing for the customer depends on it
            postJson('onCancel', { orderID: orderID || null }).catch(() => {});
        },

        fail(error) {
            if (this.basketGone) {
                return;
            }

            this.loading = false;
            this.error = errorText;
            console.error('PayPal SDK error:', error);

            postJson('onError', { error: error?.message || JSON.stringify(error) }).catch(() => {});
        },

        /** The shop only ships to its deliverable countries; PayPal shows its own message for the rest. */
        async changeShippingAddress(data, actions) {
            try {
                await postJson('onShippingAddressChange', {
                    orderID: data.orderID,
                    shippingAddress: data.shippingAddress,
                });
            } catch (error) {
                // the shop refused the address; a network failure is no reason to name the country
                if (error.status) {
                    return actions.reject(data.errors.COUNTRY_ERROR);
                }
                throw error;
            }

            return undefined;
        },
    }));
});
